'use client';

import { ClubCurrencySimpleIcon } from '@wwe-portal/ui/components/icons/club-currency-simple-icon';
import {
  createPortableDialog,
  removeDialog,
  showDialog,
  showDialogById,
  usePortableDialog,
} from '@wwe-portal/ui/components/dialog/portable-dialog';
import {
  PlayerRosterForClubStore,
  RosterLevelType,
} from '@wwe-portal/entities/roster.type';
import { FC, useMemo, useState } from 'react';

import { useSubscriptionQuery } from '@wwe-portal/web/providers/queries/subscription';
import {
  calculateFusingCost,
  useSubscriptionHistory,
} from '@wwe-portal/web/utils/club-store/helper';
import { useMutateFuseUp } from '@wwe-portal/web/providers/queries/club-store.mutation';
import { NotEnoughClubCoinDialog } from './NotEnoughClubCoinDialog';
import { useGetClubCoins } from '@wwe-portal/web/providers/queries/currency';
import { useTranslations } from 'next-intl';
import { UserSubscriptionMutationState } from '../../subscription/type';
import { RosterFuseUpDialog } from '@wwe-portal/ui/features/roster';

type FuseupDialogProps = {
  currentRoster: PlayerRosterForClubStore;
  hideRosterFilter?: () => void;
  subsribeMutation: UserSubscriptionMutationState;
  onClose: () => void;
};

const ButtonContent: FC<{
  isSubscribed: boolean;
  canFuseup: boolean;
  fuseupPrice: number;
}> = ({ isSubscribed, canFuseup, fuseupPrice }) => {
  const t = useTranslations();
  if (canFuseup && fuseupPrice > 0)
    return (
      <>
        <ClubCurrencySimpleIcon size="large" className="text-white" />
        <span>{fuseupPrice}</span>
      </>
    );

  if (canFuseup && fuseupPrice <= 0) {
    return <>{t('fuseUp_upgrade')}</>;
  }

  if (isSubscribed) return <>{t('fuseUp_require_prestige_pass')}</>;
  return <>{t('fuseUp_require_subscription')}</>;
};

export const FuseupDialog = createPortableDialog(
  ({
    currentRoster,
    hideRosterFilter,
    subsribeMutation,
    onClose,
  }: FuseupDialogProps) => {
    const { currentPlan, abilities } = useSubscriptionQuery();
    const { amount: clubCoin } = useGetClubCoins();
    const { goAddClubCoin, goToSubscription } = useSubscriptionHistory();
    const modal = usePortableDialog();
    const [selectedStar, setSelectedStar] = useState<number | undefined>(
      currentRoster.star
    );
    const [selectedLevel, setSelectedLevel] = useState<
      RosterLevelType | undefined
    >(currentRoster.level);

    const removeModal = () => {
      modal.remove();
      onClose();
    };

    const resetSelection = () => {
      setSelectedLevel(currentRoster.level);
      setSelectedStar(currentRoster.star);
    };

    const handleSelectStar = (nextStar: number) => {
      if (!abilities.clubStoreAccess) return;
      const nextFusingCost = calculateFusingCost({
        currentLevel: currentRoster.level,
        currentStar: currentRoster.star,
        nextLevel: selectedLevel ?? currentRoster.level,
        nextStar,
        hollowStar: currentRoster.hollowStar,
      });

      if (nextFusingCost <= 0) {
        resetSelection();
        return;
      }
      setSelectedStar(nextStar);
    };

    const handleSelectLevel = (nextTier: RosterLevelType) => {
      if (!abilities.clubStoreAccess) return;
      if (!currentRoster) return;
      if (
        currentRoster.level === undefined ||
        currentRoster.star === undefined ||
        currentPlan === undefined ||
        nextTier === selectedLevel ||
        selectedStar === undefined
      ) {
        return;
      }

      setSelectedLevel(nextTier);

      if (selectedStar > currentRoster.star + currentRoster.hollowStar) {
        return;
      }

      setSelectedStar(currentRoster.star + currentRoster.hollowStar);
    };

    const fuseUpPrice = useMemo(() => {
      return calculateFusingCost({
        currentLevel: currentRoster?.level,
        currentStar: currentRoster?.star,
        hollowStar: currentRoster?.hollowStar,
        nextLevel: selectedLevel ?? currentRoster?.level,
        nextStar: selectedStar ?? currentRoster?.star,
      });
    }, [selectedLevel, selectedStar, currentRoster]);

    const clearAll = () => {
      reset();
      setSelectedStar(undefined);
      setSelectedLevel(undefined);
    };

    const {
      mutate,
      isPending: isFusingUp,
      reset,
    } = useMutateFuseUp({
      onSuccess: () => {
        removeModal();
      },
      onError: () => {
        reset();
      },
      onCloseSuccessDialog: () => {
        clearAll();
      },
    });

    const handleClickUpgrade = () => {
      if (!currentRoster) return;
      if (isFusingUp || subsribeMutation.isLoading) return;

      if (!abilities.clubStoreAccess) {
        removeModal();
        hideRosterFilter?.();
        goToSubscription();
        return;
      }
      if (selectedLevel == undefined || selectedStar == undefined) {
        throw new Error('invalid data');
      }
      if (fuseUpPrice > (clubCoin ?? 0)) {
        showDialog(NotEnoughClubCoinDialog);
        return;
      }

      mutate({
        stars: selectedStar,
        level: selectedLevel,
        rosterId: currentRoster.rosterId,
        amount: fuseUpPrice,
      });
    };

    return (
      <RosterFuseUpDialog
        open={modal.visible}
        contentProps={{
          selectedLevel,
          selectedStar,
          roster: currentRoster,
          onSelectLevel: handleSelectLevel,
          onSelectStar: handleSelectStar,
          clubCoinProps: {
            clubCoin: clubCoin ?? 0,
            onClickAdd: () => {
              removeModal();
              hideRosterFilter?.();
              goAddClubCoin();
            },
          },
          fuseUpPrice,
          buttonProps: {
            onClick: handleClickUpgrade,
            disabled:
              abilities.clubStoreAccess &&
              (isFusingUp || subsribeMutation.isLoading || fuseUpPrice <= 0),
            loading: isFusingUp || subsribeMutation.isLoading,
            children: (
              <div className="flex items-center gap-1 text-base uppercase">
                <ButtonContent
                  isSubscribed={currentPlan != null}
                  canFuseup={abilities.clubStoreAccess}
                  fuseupPrice={fuseUpPrice}
                />
              </div>
            ),
          },
          isUpgradeMode: true,
        }}
        onClose={() => {
          if (isFusingUp || subsribeMutation.isLoading) {
            return;
          }
          removeModal();
        }}
      />
    );
  }
);

const ID = 'FuseupDialog';
export const useFuseupDialog = ({
  currentRoster,
  ...props
}: Omit<FuseupDialogProps, 'currentRoster'> & {
  currentRoster: PlayerRosterForClubStore | undefined;
}) => {
  return {
    show: () => showDialogById(ID),
    remove: () => {
      removeDialog(ID);
      props.onClose();
    },
    dialog: currentRoster ? (
      <FuseupDialog id={ID} currentRoster={currentRoster} {...props} />
    ) : null,
  };
};
